import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './app/App';
import './app/styles.css';
import { announceReady, webApp, type ThemeParams } from './telegram/webapp';

/**
 * Точка входа (T11.1).
 *
 * ## Тема переносится в переменные CSS, а не в пропсы
 *
 * Иначе каждый компонент, которому нужен цвет, тянул бы контекст темы, а
 * при её смене перерисовывалось бы всё дерево. Переменные меняются в
 * одном месте, и браузер перекрашивает сам.
 *
 * Тема применяется до первой отрисовки. Примени мы её после — человек
 * увидел бы вспышку чужих цветов на светлом устройстве, а это первое,
 * что он увидит вообще.
 */

/** Какое поле темы Telegram в какую переменную ложится. */
const PALETTE: readonly (readonly [keyof ThemeParams, string])[] = [
  ['bg_color', '--bg'],
  ['secondary_bg_color', '--bg-elevated'],
  ['text_color', '--text'],
  ['hint_color', '--hint'],
  ['button_color', '--accent'],
  ['button_text_color', '--accent-text'],
];

function applyTheme(): void {
  const app = webApp();

  if (app === null) return;

  const root = document.documentElement;

  for (const [field, variable] of PALETTE) {
    const value = app.themeParams[field];

    // Пустое поле пропускается, а не записывается: Telegram присылает не
    // все цвета на всех платформах, и пустая переменная сделала бы текст
    // невидимым вместо того, чтобы оставить наш цвет
    if (value !== undefined && value !== '') root.style.setProperty(variable, value);
  }

  root.style.colorScheme = app.colorScheme;
}

applyTheme();
announceReady();

// Тему меняют прямо во время работы — переключателем в самом Telegram.
// Не подпишись мы, приложение осталось бы тёмным на светлом устройстве
// до перезапуска
webApp()?.onEvent('themeChanged', applyTheme);

const root = document.getElementById('root');

if (root === null) {
  throw new Error('Не найден корневой элемент');
}

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
